import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { useSelector } from "react-redux";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import PackageDataFilter from "./PackageDataFilter";
import { PackageResponse } from "../../app/models/responseModels/packageResponse";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { getPackageList } from "../../app/features/package/packageSlice";
import { PACKAGE_COLUMN_NAME, PACKAGE_LABEL_NAME } from "./PackageConstants";
import PackageRecipientDetailsTooltip from "./PackageRecipientDetailsTooltip";
import { useSubmitModalWithParameter } from "../../components/common/modalPopup/useSubmitModal";
import EditPackageModal from "./modals/EditPackageModal";
import * as XLSX from "xlsx";
import { convertArrayToCSV, downloadCsv, DownloadFileModel } from "../../helpers/downloadFile";

const breadcrumb = ["Packages"];

const Packages = () => {
  const dispatch = useAppDispatch();
  const { isLoading, packageList } = useSelector(
    (state: RootState) => state.package
  );

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [filteredData, setFilteredData] = useState<PackageResponse[]>([]);

  // ***** Callbacks *****
  const getPackageData = useCallback(async () => {
    try {
      const response = await dispatch(getPackageList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "packageList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "packageListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getPackageData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getPackageData]);

  useEffect(() => {
    if (packageList.length > 0) {
      if (filteredData.length === packageList.length) {
        setFilteredData(packageList);
      }
    } else {
      setFilteredData([]);
    }
  }, [packageList, filteredData]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<PackageResponse>[]>(
    () => [
      {
        accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_NAME,
        header: PACKAGE_LABEL_NAME.SHIPMENT_CLIENT_NAME,
        size: 120,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME,
        header: PACKAGE_LABEL_NAME.PARCEL_CENTER_LOCATION_NAME,
        size: 120,
      },

      {
        accessorKey: PACKAGE_COLUMN_NAME.FDP_COMPANY_NAME,
        header: PACKAGE_LABEL_NAME.FDP_COMPANY_NAME,
        size: 200,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.SERVICE_TYPE_NAME,
        header: PACKAGE_LABEL_NAME.SERVICE_TYPE_NAME,
        size: 80,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.FDP_ID,
        header: PACKAGE_LABEL_NAME.FDP_ID,
        size: 50,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.TRACKING_NUMBER,
        header: PACKAGE_LABEL_NAME.TRACKING_NUMBER,
        size: 200,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.STATUS,
        header: PACKAGE_LABEL_NAME.STATUS,
        size: 80,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.RECEIVED_DATE,
        header: PACKAGE_LABEL_NAME.RECEIVED_DATE,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            <span>
              {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h24",
              })}
            </span>
          </div>
        ),
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.PLANNED_DELIVERY_DATE,
        accessorFn: (row) => new Date(row.deliveryDate),
        header: PACKAGE_LABEL_NAME.PLANNED_DELIVERY_DATE,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            <span>
              {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h24",
              })}
            </span>
          </div>
        ),
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.PARCEL_ARRIVAL_DATE,
        accessorFn: (row) => new Date(row.parcelArrivalDate),
        header: PACKAGE_LABEL_NAME.PARCEL_ARRIVAL_DATE,
        size: 120,
        Cell: ({ cell, row }) => (
          <div>
            <span>
              {new Date(cell.getValue<string>()).toLocaleDateString()}
            </span>
          </div>
        ),
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.DELIVERY_DATE,
        accessorFn: (row) => new Date(row.deliveryDate),
        header: PACKAGE_LABEL_NAME.DELIVERY_DATE,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            <span>
              {new Date(cell.getValue<string>()).toLocaleDateString()}
            </span>
          </div>
        ),
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.PACKAGE_VOLUME,
        header: PACKAGE_LABEL_NAME.PACKAGE_VOLUME,
        Cell: ({cell, row}) => (
          <div>
            <span>{row.original.packageVolume ? row.original.packageVolume: "NA"}</span>
          </div>
        ),
        size: 80,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.PACKAGE_WEIGHT,
        header: PACKAGE_LABEL_NAME.PACKAGE_WEIGHT,
        size: 80,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.TWO_MAN,
        header: PACKAGE_LABEL_NAME.TWO_MAN,
        size: 50,
        Cell: ({ cell, row }) => (
          <div>
            <span>{row.original.twoMan === 1 ? "Y" : "N"}</span>
          </div>
        ),
      },
      {
        accessorKey: "shipmentRecipientName",
        header: "Recipient Details",
        size: 120,
        Cell: ({ cell, row }) => (
          <div>
            <PackageRecipientDetailsTooltip
              packageData={row.original}
            ></PackageRecipientDetailsTooltip>
          </div>
        ),
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID,
        header: PACKAGE_LABEL_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID,
        size: 200,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.PARCEL_CENTER_ID,
        header: PACKAGE_LABEL_NAME.PARCEL_CENTER_ID,
        size: 150,
      },
      {
        accessorKey: PACKAGE_COLUMN_NAME.STATUS,
        header: PACKAGE_LABEL_NAME.STATUS,
        size: 80,
      },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_EXTERNAL_PARTNER_SHIPMENT_ID,
      //   size: 200,
      // },

      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.BAR_CODE,
      //   header: PACKAGE_LABEL_NAME.BAR_CODE,
      //   size: 200,
      // },

      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.DESKTOP,
      //   header: PACKAGE_LABEL_NAME.DESKTOP,
      //   size: 50,
      //   Cell: ({ cell, row }) => (
      //     <div>
      //       <span>{row.original.twoMan === "1" ? "Y" : "N"}</span>
      //     </div>
      //   ),
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_DELIVERY_INSTRUCTIONS,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_DELIVERY_INSTRUCTIONS,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESS_TYPE,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_ADDRESS_TYPE,
      //   size: 80,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SIGNATURE_REQUIRED,
      //   header: PACKAGE_LABEL_NAME.SIGNATURE_REQUIRED,
      //   size: 80,
      // },

      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_NAME,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_NAME,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_PHONE,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_PHONE,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_1,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_1,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_2,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_ADDRESSLINE_2,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_CITY,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_CITY,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_STATE,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_STATE,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_RECIPIENT_ZIP,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_RECIPIENT_ZIP,
      //   size: 200,
      // },
      // {
      //   accessorKey: "packageVolume",
      //   header: "Package Details",
      //   size: 100,
      //   Cell: ({ cell, row }) => (
      //     <div>
      //       <PackageDetailsTooltip
      //         packageData={row.original}
      //       ></PackageDetailsTooltip>
      //     </div>
      //   ),
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.PACKAGE_VOLUMN,
      //   header: PACKAGE_LABEL_NAME.PACKAGE_VOLUMN,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.PACKAGE_LENGTH,
      //   header: PACKAGE_LABEL_NAME.PACKAGE_LENGTH,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.PACKAGE_WIDTH,
      //   header: PACKAGE_LABEL_NAME.PACKAGE_WIDTH,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.PACKAGE_HEIGHT,
      //   header: PACKAGE_LABEL_NAME.PACKAGE_HEIGHT,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.PACKAGE_WEIGHT,
      //   header: PACKAGE_LABEL_NAME.PACKAGE_WEIGHT,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.PACKAGE_CONTENTS,
      //   header: PACKAGE_LABEL_NAME.PACKAGE_CONTENTS,
      //   size: 200,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.STATUS_DATE,
      //   header: PACKAGE_LABEL_NAME.STATUS_DATE,
      //   size: 120,
      //   Cell: ({ cell, row }) => (
      //     <div>
      //       <span>
      //         {new Date(cell.getValue<string>()).toLocaleDateString()}
      //       </span>
      //     </div>
      //   ),
      // },
      // {
      

      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_EXTERNAL_ORDER_ID,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_EXTERNAL_ORDER_ID,
      //   size: 180,
      // },

      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.SHIPMENT_CLIENT_ID,
      //   header: PACKAGE_LABEL_NAME.SHIPMENT_CLIENT_ID,
      //   size: 80,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.CREATED_ON,
      //   header: PACKAGE_LABEL_NAME.CREATED_ON,
      //   size: 180,
      //   Cell: ({ cell, row }) => (
      //     <div>
      //       <span>
      //         {new Date(cell.getValue<string>()).toLocaleString("en-US", {
      //           hourCycle: "h24",
      //         })}
      //       </span>
      //     </div>
      //   ),
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.CREATED_BY,
      //   header: PACKAGE_LABEL_NAME.CREATED_BY,
      //   size: 150,
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.UPDATED_ON,
      //   header: PACKAGE_LABEL_NAME.UPDATED_ON,
      //   size: 180,
      //   Cell: ({ cell, row }) => (
      //     <div>
      //       <span>
      //         {new Date(cell.getValue<string>()).toLocaleString("en-US", {
      //           hourCycle: "h24",
      //         })}
      //       </span>
      //     </div>
      //   ),
      // },
      // {
      //   accessorKey: PACKAGE_COLUMN_NAME.UPDATED_BY,
      //   header: PACKAGE_LABEL_NAME.UPDATED_BY,
      //   size: 150,
      // },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getPackageData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function getFilteredDataFileMapping() {
  return filteredData.map((pkg) => ({
    Client: pkg.shipmentClientName,
    "Parcel Center": pkg.parcelCenterLocationName,
    FDP: pkg.fdpCompanyName,
    "Service Type": pkg.serviceTypeName,
    "FDP ID": pkg.fdpId,
    "Shipment External Partner": pkg.shipmentExternalPartnerShipmentId,
    "Shipment ID": pkg.shipmentId,
    "Tracking Number": pkg.trackingNumber,
    "Bar Code": pkg.barCode,
    "Manifest Received Date": pkg.receivedDate,
    "Planned Delivery Date": pkg.plannedDeliveryDate,
    "Actual Arrival Date": pkg.parcelArrivalDate,
    "Scheduled Delivery Date": pkg.deliveryDate,
    "Two Man": pkg.twoMan,
    Desktop: pkg.desktop,
    "Delivery Instructions": pkg.shipmentDeliveryInstructions,
    "Address Type": pkg.shipmentRecipientAddressType,
    "Signature Required": pkg.signatureRequired,
    "Recipient Name": pkg.shipmentRecipientName,
    "Recipient Phone": pkg.shipmentRecipientPhone,
    "Recipient Address 1": pkg.shipmentRecipientAddressLine1,
    "Recipient Address 2": pkg.shipmentRecipientAddressLine2,
    "Recipient City": pkg.shipmentRecipientCity,
    "Recipient State": pkg.shipmentRecipientState,
    "Recipient Zip": pkg.shipmentRecipientZip,
    "Package Volume": pkg.packageVolume,
    "Package Length": pkg.packageLength,
    "Package Width": pkg.packageWidth,
    "Package Height": pkg.packageHeight,
    "Package Weight": pkg.packageWeight,
    "Package Contents": pkg.packageContents,
    "Status Date": pkg.statusDate,
    Status: pkg.status,
    "Shipment External Order ID": pkg.shipmentExternalOrderId,
    "FDC Parcel Center ID": pkg.parcelCenterId,
    "Client ID": pkg.shipmentClientId,
    "Created On": pkg.createdOn,
    "Created By": pkg.createdBy,
    "Updated On": pkg.updatedOn,
    "Updated By": pkg.updatedBy,
  }));
}

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    try {
          // Transform tableData into CSV format
          const csvData = getFilteredDataFileMapping();
          // Convert CSV data to CSV string
          const csvContent = convertArrayToCSV(csvData);
          // Download the CSV
          const file: DownloadFileModel = {
            data: csvContent,
            contentType: "text/csv",
            fileName: `Packages-Export-${Date.now()}.csv`,
          };
          downloadCsv(file);
        } catch (error) {
          toast.error("unable to download file", {
            toastId: "FdpuserDownloadCsv",
          });
        }
    handleClose();
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    if(filteredData.length === 0 || !filteredData) {
      toast.warning("No Data available to export.", {
        toastId: "packageDownloadEmpty"
      })
      return;
    }
    const columnNames = [
    "Client",
    "Parcel Center",
    "FDP",
    "Service Type",
    "FDP ID",
    "Shipment External Partner",
    "Shipment ID",
    "Tracking Number",
    "Bar Code",
    "Manifest Received Date",
    "Planned Delivery Date",
    "Actual Arrival Date",
    "Scheduled Delivery Date",
    "Two Man",
    "Desktop",
    "Delivery Instructions",
    "Address Type",
    "Signature Required",
    "Recipient Name",
    "Recipient Phone",
    "Recipient Address 1",
    "Recipient Address 2",
    "Recipient City",
    "Recipient State",
    "Recipient Zip",
    "Package Volume",
    "Package Length",
    "Package Width",
    "Package Height",
    "Package Weight",
    "Package Contents",
    "Status Date",
    "Status",
    "Shipment External Order ID",
    "FDC Parcel Center ID",
    "Client ID",
    "Created On",
    "Created By",
    "Updated On",
    "Updated By",
  ];

  const dataWithHeaders = filteredData.map((row) => ({
    "Client": row.shipmentClientName,
    "Parcel Center": row.parcelCenterLocationName,
    "FDP": row.fdpCompanyName,
    "Service Type": row.serviceTypeName,
    "FDP ID": row.fdpId,
    "Shipment External Partner": row.shipmentExternalPartnerShipmentId,
    "Shipment ID": row.shipmentId,
    "Tracking Number": row.trackingNumber,
    "Bar Code": row.barCode,
    "Manifest Received Date": row.receivedDate,
    "Planned Delivery Date": row.plannedDeliveryDate,
    "Actual Arrival Date": row.parcelArrivalDate,
    "Scheduled Delivery Date": row.deliveryDate,
    "Two Man": row.twoMan,
    "Desktop": row.desktop,
    "Delivery Instructions": row.shipmentDeliveryInstructions,
    "Address Type": row.shipmentRecipientAddressType,
    "Signature Required": row.signatureRequired,
    "Recipient Name": row.shipmentRecipientName,
    "Recipient Phone": row.shipmentRecipientPhone,
    "Recipient Address 1": row.shipmentRecipientAddressLine1,
    "Recipient Address 2": row.shipmentRecipientAddressLine2,
    "Recipient City": row.shipmentRecipientCity,
    "Recipient State": row.shipmentRecipientState,
    "Recipient Zip": row.shipmentRecipientZip,
    "Package Volume": row.packageVolume,
    "Package Length": row.packageLength,
    "Package Width": row.packageWidth,
    "Package Height": row.packageHeight,
    "Package Weight": row.packageWeight,
    "Package Contents": row.packageContents,
    "Status Date": row.statusDate,
    "Status": row.status,
    "Shipment External Order ID": row.shipmentExternalOrderId,
    "FDC Parcel Center ID": row.parcelCenterId,
    "Client ID": row.shipmentClientId,
    "Created On": row.createdOn,
    "Created By": row.createdBy,
    "Updated On": row.updatedOn,
    "Updated By": row.updatedBy,
  }));

  const worksheetData = [
    columnNames,
    ...dataWithHeaders.map((row) =>
  columnNames.map((col) => (row as Record<string, any>)[col] ?? "")
),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Package List");

  XLSX.writeFile(
    workbook,
    `Package-Export-${Date.now()}.xlsx`,
    { compression: true }
  );
  };

  // ***** Modal Popups *****
  const [editPackageModal, openEditPackageModal] =
    useSubmitModalWithParameter<PackageResponse>(
      (packageRowData, closeEditPackageModal) => (
        <EditPackageModal
          closeEditPackageModal={closeEditPackageModal}
          onCompleted={getPackageData}
          packageRowData={packageRowData}
        />
      )
    );

  // ***** Datagrid Custom Actions *****

  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<PackageResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <PackageDataFilter
          packageData={packageList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No package packages"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={true}
        defaultSortColumnName="deliveryDate"
        openModal={openEditPackageModal}
      />
      {editPackageModal}
    </Box>
  );
};

export default Packages;
